import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Supported namespaces for translations
 * Each namespace corresponds to a JSON file in public/locales/{lang}/
 */
export const NAMESPACES = [
	'common',
	'menu',
	'dashboard',
	'organization',
	'projects',
	'contracts',
	'talents',
	'team',
	'invoices',
	'timesheets',
	'messages',
	'profile',
	'settings',
	'billing',
	'legal',
	'subscription',
	'features',
	'validation',
	'errors',
	'quotes', // deprecated, kept for backward compatibility
	'translation', // legacy, will be removed after full migration
] as const;

export type Namespace = (typeof NAMESPACES)[number];

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = ['it', 'en', 'fr', 'de', 'es', 'pt', 'pl'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

i18n
	// load translation using http -> see /public/locales
	.use(Backend)
	// detect user language
	.use(LanguageDetector)
	// pass the i18n instance to react-i18next
	.use(initReactI18next)
	// init i18next
	// for all options read: https://www.i18next.com/overview/configuration-options
	.init({
		// Language configuration
		fallbackLng: 'it',
		supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],

		// Debug mode in development
		debug: process.env.NODE_ENV === 'development',

		// Namespace configuration
		ns: NAMESPACES as unknown as string[],
		defaultNS: 'common',
		fallbackNS: ['common', 'translation'], // Fallback to common, then legacy translation

		// Backend configuration for loading translation files
		backend: {
			loadPath: '/locales/{{lng}}/{{ns}}.json',
		},

		interpolation: {
			escapeValue: false, // not needed for react as it escapes by default
		},

		react: {
			useSuspense: true,
			bindI18n: 'languageChanged loaded',
		},

		// Missing key handling (development only)
		saveMissing: process.env.NODE_ENV === 'development',
		missingKeyHandler: (lngs, ns, key) => {
			if (process.env.NODE_ENV === 'development') {
				console.warn(
					`[i18n] Missing translation: ${ns}:${key} for languages: ${lngs.join(', ')}`,
				);
			}
		},
	});

export default i18n;
